/**
 * The hideout (WP-X4, docs/HIDEOUT.md): a gang's home behind the door of 9 Pilcher Street (the safehouse POI),
 * furnished from the gang treasury. Pure rules shared by the server (the only thing that applies them) and the
 * client (which only shows them).
 *
 * STATUS, NEVER POWER: nothing here changes health, damage, speed, carrying, prices, heat or any validation
 * limit. A furnishing is something members SEE (a bar, a jukebox, a pool table, the gang's honours on the wall);
 * the livery is paint (a car's trim in the gang's colour), applied at Pike's like any other spray-bay work.
 * Each furnishing is bought once per gang, so the treasury can be spent here only as far as the catalogue goes.
 */

export const HIDEOUT_ITEM_IDS = ['sofa', 'photos', 'jukebox', 'trophies', 'pool', 'bar', 'livery'] as const;
export type HideoutItemId = (typeof HIDEOUT_ITEM_IDS)[number];
export const isHideoutItem = (v: string): v is HideoutItemId => (HIDEOUT_ITEM_IDS as readonly string[]).includes(v);

export interface HideoutItemDef {
  id: HideoutItemId;
  name: string;
  /** Pounds out of the treasury, once (into the `hideout-furnish` sink). */
  price: number;
  /** One plain line: what it is and what members get from it. */
  blurb: string;
}

/**
 * The catalogue, cheapest first (the order the hideout's panel lists them). Prices: £1,000–£6,000, £18,000 in
 * all — a few good freight evenings for a gang, the same order as a fitted premises (shared/properties.ts), so
 * a gang furnishes over weeks, not in one night, and a finished hideout says the gang has been about.
 */
export const HIDEOUT_ITEMS: readonly HideoutItemDef[] = [
  { id: 'sofa', name: 'Carpet, lamps and a sofa', price: 1_000, blurb: 'A rug on the boards, two standard lamps and a big brown sofa. Somewhere to sit.' },
  { id: 'photos', name: 'The emblem and the photos', price: 1_000, blurb: 'The gang’s emblem over the mantelpiece in its colours, and a framed photo of every member by the window.' },
  { id: 'jukebox', name: 'The jukebox', price: 1_500, blurb: 'A lit-up jukebox wired to the wireless: pick the station, it plays while you’re in.' },
  { id: 'trophies', name: 'The trophy wall', price: 2_000, blurb: 'The gang’s honours from the Evening Post and its history — districts run, contests won, freight carried — on the wall.' },
  { id: 'pool', name: 'The pool table', price: 2_500, blurb: 'A full-size table under a green-shaded lamp.' },
  { id: 'bar', name: 'The bar', price: 4_000, blurb: 'A proper bar: a mahogany counter, optics, a brass rail and four stools.' },
  { id: 'livery', name: 'The gang livery', price: 6_000, blurb: 'The gang’s colour as a livery: members can have their own car done at Pike’s spray bay (stripes, a roof or a band in the gang’s colour).' },
];

export const HIDEOUT_TOTAL = HIDEOUT_ITEMS.reduce((n, i) => n + i.price, 0);

export function hideoutItem(id: string): HideoutItemDef | undefined {
  return HIDEOUT_ITEMS.find((i) => i.id === id);
}

/** The radio stations the jukebox can play (ids of src/audio/radio/stations.ts; a test keeps them in step). */
export const JUKEBOX_STATIONS = ['soul', 'beat', 'easy', 'talk', 'ska'] as const;
export type JukeboxStation = (typeof JUKEBOX_STATIONS)[number];
export const isJukeboxStation = (v: string): v is JukeboxStation => (JUKEBOX_STATIONS as readonly string[]).includes(v);

export const HIDEOUT = {
  /** The door: the safehouse POI (9 Pilcher Street). Its own safe zone covers it (shared/limits.ts, 12 m). */
  poi: 'safehouse',
  /** How far from the POI a member may be to go in (m): the door, plus slack for lag. */
  reach: 8,
  /** Further than this from the POI and the server takes you out of the room (you walked off). */
  leaveReach: 14,
  /** Pike's labour for a member's car in the gang livery (the member pays, bank first). Taking it off: `liveryOff`. */
  liveryFee: 150,
  liveryOff: 30,
  /** Lines of contest history on the wall. */
  historyMax: 12,
  /** Honours on the wall (newest first). */
  trophiesMax: 24,
} as const;

/** Car models that carry a trim the livery can go on (coupe stripes, saloon vinyl roof, hatch roof, van band). */
export const LIVERY_SPECS: readonly string[] = ['compact', 'saloon', 'van', 'coupe', 'estate', 'exec'];
